import L, { LatLngExpression } from 'leaflet'
import { memo, useLayoutEffect, useMemo, useState } from 'react'
import { Marker, Tooltip, useMap } from 'react-leaflet'
import locationIcon from 'src/assets/svg/location.svg'
import locationDeviationIcon from 'src/assets/svg/location_deviation.svg'
import { ICluster } from 'src/generated-types/cluster'
import { getMooringName } from 'src/screens/localities/moorings/positionTableHelper'
import { getFormattedDate } from 'src/utils'
import { IMooring, IMooringType, IPositionType } from '../../../../../generated-types'
import { CreateLayerGroup } from '../ComponentLayer'
import { minZoom } from '../constraints'
import { validatePosition } from '../MapBoundsComponent'
import { midpoint } from '../utils'

export const CreateMooring: React.FC<{
  mooring: IMooring
  type?: IPositionType
  mapPosition: L.LatLngExpression
  removeType?: IMooringType
  isFleet?: boolean
  isModalView: boolean
  navigateLocality?: () => void
  clusters?: ICluster[]
  showCageStatusPopOver?: boolean
  onPositionSelect?: (positionId: number, positionName: string) => void
  mooringsLength?: number
  showCriticalPositions?: boolean
}> = memo(
  ({
    mooring,
    type,
    clusters,
    mapPosition,
    removeType = null,
    isFleet,
    isModalView,
    navigateLocality,
    showCageStatusPopOver,
    onPositionSelect,
    mooringsLength,
    showCriticalPositions,
  }) => {
    const map = useMap()
    const [viewContent, setViewContent] = useState(map.getZoom() > minZoom ? true : false)
    const center = useMemo(() => {
      const positions = mooring.positions
      if (positions) {
        const corners = positions.filter(p => p.type == IPositionType.Corner)
        if (corners.length > 2) {
          return midpoint(
            corners[0].latitude,
            corners[0].longitude,
            corners[corners.length - 1].latitude,
            corners[corners.length - 1].longitude,
            0.5,
          ) as LatLngExpression
        }
        if (corners[0]?.latitude && corners[0]?.longitude) {
          return [corners[0]?.latitude, corners[0]?.longitude] as LatLngExpression
        }
      }
      return [0, 0] as LatLngExpression
    }, [mooring.positions])

    const isCluster = useMemo(() => {
      if (clusters && clusters?.length > 0) {
        const insideClusterIndex = clusters.findIndex(
          c => c.localities.findIndex(m => m.id == mooring.localityId) != -1,
        )

        if (insideClusterIndex != -1) {
          return true
        }
      }

      return false
    }, [clusters, mooring, map])

    useLayoutEffect(() => {
      const updateViewContent = () => {
        setViewContent(map.getZoom() >= minZoom)
      }
      updateViewContent()
      map.addEventListener('zoom', updateViewContent)
      return () => {
        map.removeEventListener('zoom', updateViewContent)
      }
    }, [mooringsLength])

    const circleEvent = useMemo(() => {
      return {
        click() {
          if (navigateLocality) {
            navigateLocality()
          } else {
            map.fitBounds(
              (mooring?.positions?.filter(x => validatePosition(x)) || []).map(p => [p.latitude, p.longitude]),
              {
                animate: false,
                duration: 100,
                padding: [20, 20],
                maxZoom: 16,
              },
            )
          }
        },
      }
    }, [center, navigateLocality])

    if ((removeType && mooring.type == removeType) || isCluster == true) {
      return <></>
    }

    if (viewContent) {
      return (
        <CreateLayerGroup
          positions={mooring.positions}
          type={type}
          mapPosition={mapPosition}
          isFleet={mooring.type == IMooringType.Barge || isFleet}
          steelFrame={mooring.type == IMooringType.SteelCage}
          isModalView={isModalView}
          clusters={clusters}
          showCageStatusPopOver={showCageStatusPopOver}
          mooringType={mooring?.type}
          onPositionSelect={onPositionSelect}
          showCriticalPositions={showCriticalPositions}
        />
      )
    } else {
      if (center) {
        return (
          <Marker
            eventHandlers={circleEvent}
            position={center}
            icon={L.icon({
              iconUrl:
                mooring?.deviationsCount > 0 ||
                mooring?.positions?.some(p => p.mainComponents?.some(mc => mc.activeDeviationsCount ?? 0 > 0))
                  ? locationDeviationIcon
                  : locationIcon,
              iconSize: [25, 38],
            })}
          >
            <Tooltip direction={'top'} offset={[0, -10]}>
              {getMooringName(mooring.type as IMooringType)} {getFormattedDate(mooring.installationDate) ?? 'N/A'}
            </Tooltip>
          </Marker>
        )
      }
    }
    return <></>
  },
)
